import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { SignOutButton } from "@/core/auth/components/SignOutButton";
import { authService } from "@/core/auth/services/authService";
import { useBusinesses } from "@/features/business/hooks/businessQueries";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { ItemList } from "@/shared/components/ui/ItemList";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { SubscriptionManagement } from "../components/SubscriptionManagement";
import { useSubscription } from "../hooks/subscriptionQueries";
import { isBusinessIdParam } from "../utils/subscriptionUrls";

const outcomes = {
  success: "Thanks! Stripe is confirming your payment. Your new plan appears here and in the Froppio app as soon as it is confirmed.",
  cancel: "Checkout was canceled. You were not charged and your plan has not changed.",
  portal: "You returned from Stripe. Any changes to your plan appear as soon as Stripe confirms them.",
} as const;

type Outcome = keyof typeof outcomes;
const isOutcome = (value: string | null): value is Outcome => !!value && Object.hasOwn(outcomes, value);

/**
 * Standalone subscription management (app.fropp.io/manage-subscription, reached via fropp.io).
 * It deliberately has no app navigation: it only manages the plan of a business the user owns.
 */
export function ManageSubscriptionPage() {
  const { user, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [outcome] = useState(() => {
    const value = searchParams.get("outcome");
    return isOutcome(value) ? value : null;
  });
  const host = authService.kind === "host";
  const businesses = useBusinesses();
  const owned = (businesses.data ?? []).filter((business) => !!user && business.ownerId === user.id);
  const requestedId = searchParams.get("business_id");
  const requested = isBusinessIdParam(requestedId) ? owned.find((business) => business.id === requestedId) : undefined;
  const selected = requested ?? (owned.length === 1 && !requestedId ? owned[0] : undefined);
  const query = useSubscription(selected?.id, user?.id, !host && !!selected);

  useEffect(() => {
    if (!searchParams.has("outcome")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("outcome");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const select = (businessId: string | null) =>
    setSearchParams(businessId ? { business_id: businessId } : {}, { replace: true });

  return (
    <div className="h-dvh">
      <Page>
        <PageHeader
          title="Manage subscription"
          homeLink={false}
          actions={(
            <>
              {selected && !host && (
                <PageHeaderAction onClick={() => void query.refetch()} disabled={query.isFetching}>
                  {query.isFetching ? "Refreshing..." : "Refresh"}
                </PageHeaderAction>
              )}
              <SignOutButton signOut={signOut} />
            </>
          )}
        />
        <PageContent className="space-y-6">
          {outcome && <p role="status" className="text-sm leading-relaxed">{outcomes[outcome]}</p>}
          {host ? (
            <EmptyState
              title="Continue in your browser"
              message="Subscriptions can't be managed in the Froppio app. Open Manage subscription from your plan to continue on the Froppio website."
            />
          ) : businesses.isPending ? (
            <LoadingSpinner message="Loading your businesses..." />
          ) : businesses.error ? (
            <ErrorState message="Your businesses are unavailable. Please try again." onRetry={() => void businesses.refetch()} />
          ) : owned.length === 0 ? (
            <EmptyState
              title="No business to manage"
              message="Only a business owner can manage its subscription. Ask the business owner to manage it."
            />
          ) : selected && user ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm">
                  Business: <span className="font-bold" data-telemetry-mask>{selected.name}</span>
                </p>
                {owned.length > 1 && <Button variant="ghost" size="sm" onClick={() => select(null)}>Change business</Button>}
              </div>
              <SubscriptionManagement key={`${selected.id}:${user.id}`} businessId={selected.id} userId={user.id} />
            </>
          ) : (
            <Section title="Choose a business">
              {requestedId && (
                <p role="alert" className="text-sm text-danger">This business is not available in your account. Choose one you own.</p>
              )}
              <ItemList
                divided
                items={owned}
                renderItem={(business) => (
                  <Button variant="ghost" className="h-auto w-full justify-between rounded-none px-4 py-3 text-left" onClick={() => select(business.id)}>
                    <span className="min-w-0 truncate" data-telemetry-mask>{business.name}</span>
                    <ChevronRight aria-hidden="true" />
                  </Button>
                )}
              />
            </Section>
          )}
        </PageContent>
      </Page>
    </div>
  );
}
